/**
 * ProjectStatusControl — the project-scope sibling of TaskStatusControl.
 *
 * Leo (2026-08-11 feedback session): from the project screen he couldn't
 * start/move a project — the status was a read-only badge and the crew's
 * Start/Finish screen was unreachable. Supervisors/admins get the same
 * chip-with-menu idiom they already use on tasks, limited to the WORKING
 * range the server enforces (`assertProjectTransitionAllowed`): scheduled ↔
 * en_route ↔ on_site ↔ punch ↔ pending_closeout. Approve / close / block /
 * cancel keep their own flows, so they're never offered here.
 *
 * Only rendered interactive for DIRECTLY-driven projects (direct crew blocks,
 * or no tasks at all) — on a task-driven project the rollup steers status and
 * a manual move would just be fought by the next task change; those keep the
 * static badge.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import type { ProjectStatus } from '@vari-crew/schemas';
import { PROJECT_STATUS_LABELS } from '@vari-crew/schemas';
import { isQueuedProjectResult, useTransitionProject } from '@/api/project-transitions';
import { PROJECT_STATUS_CLASSES, StatusBadge } from '@/components/ui/StatusBadge';
import { cn } from '@/lib/cn';

/** The working lifecycle, in forward order — mirrors WORKING_PROJECT_STATUSES. */
const WORKING_ORDER = ['scheduled', 'en_route', 'on_site', 'punch', 'pending_closeout'] as const;
type WorkingStatus = (typeof WORKING_ORDER)[number];

/**
 * Supervisor targets from `current`: anywhere else within the working range.
 * Outside the range (requested / complete / blocked / cancelled) the server
 * refuses the crew-flow endpoint, so no menu is offered at all.
 */
export function projectTargetsFor(current: ProjectStatus): WorkingStatus[] {
  if (!(WORKING_ORDER as readonly string[]).includes(current)) return [];
  return WORKING_ORDER.filter((s) => s !== current);
}

export function ProjectStatusControl({
  projectId,
  status,
  enabled,
  className,
}: {
  projectId: string;
  status: ProjectStatus;
  /** canManage && directly-driven — anything else renders the plain badge. */
  enabled: boolean;
  className?: string;
}) {
  const transition = useTransitionProject(projectId);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const targets = enabled ? projectTargetsFor(status) : [];

  // Outside-click + Escape close (mirrors TaskStatusControl).
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (targets.length === 0) {
    return <StatusBadge status={status} className={className} />;
  }

  const currentRank = (WORKING_ORDER as readonly string[]).indexOf(status);

  function choose(to: WorkingStatus) {
    setOpen(false);
    transition.mutate(
      { to },
      {
        onSuccess: (r) =>
          toast.success(
            isQueuedProjectResult(r) ? 'Saved — will sync when online' : 'Project status updated',
          ),
        onError: (e) => toast.error(e.message),
      },
    );
  }

  return (
    <div ref={wrapRef} className={cn('relative inline-block', className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={transition.isPending}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Status: ${PROJECT_STATUS_LABELS[status]}. Change status`}
        className={cn(
          'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium transition-opacity',
          'hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 ring-offset-bg',
          'disabled:cursor-default disabled:opacity-60',
          PROJECT_STATUS_CLASSES[status],
        )}
      >
        {PROJECT_STATUS_LABELS[status]}
        <ChevronDown className="w-3.5 h-3.5 opacity-70" />
      </button>

      {open && (
        <div
          role="menu"
          id={menuId}
          aria-label="Change project status"
          className="absolute left-0 z-50 mt-1 min-w-44 overflow-hidden rounded-md border border-line bg-bg py-1 shadow-lg"
        >
          {targets.map((to) => {
            const isBackward = WORKING_ORDER.indexOf(to) < currentRank;
            return (
              <button
                key={to}
                type="button"
                role="menuitem"
                onClick={() => choose(to)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-ink hover:bg-fill-soft"
              >
                <span
                  className={cn(
                    'inline-block h-2 w-2 shrink-0 rounded-full',
                    PROJECT_STATUS_CLASSES[to],
                  )}
                  aria-hidden
                />
                <span className="flex-1">{PROJECT_STATUS_LABELS[to]}</span>
                {isBackward && (
                  <RotateCcw className="w-3 h-3 shrink-0 text-ink-faint" aria-label="revert" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
